import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { chatSocketClient, ChatMessagePayload, ChatSuspendedPayload } from '@/lib/chat-socket';
import { storage } from '@/lib/storage';
import { useGlobalChatEnabled } from '@/hooks/queries/useUserInitQuery';
import { useGlobalChatEnabledLive } from '@/hooks/useGlobalChatEnabledLive';

const MESSAGE_TTL_MS = 15 * 60 * 1000; // 15 minutes
const CHAT_OPEN_KEY = 'chat-panel-open';
const CHAT_RULES_KEY = 'chat_rules_accepted';

interface ChatContextValue {
  messages: ChatMessagePayload[];
  onlineCount: number;
  unreadCount: number;
  isOpen: boolean;
  isMobileOpen: boolean;
  isConnected: boolean;
  hasAcceptedRules: boolean;
  isSuspended: boolean;
  suspensionExpiresAt: string | null;
  sendMessage: (message: string) => Promise<{ success: boolean; error?: string }>;
  flagMessage: (messageId: number) => Promise<{ success: boolean; error?: string }>;
  unflagMessage: (messageId: number) => Promise<{ success: boolean; error?: string }>;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  toggleMobileChat: () => void;
  openMobileChat: () => void;
  closeMobileChat: () => void;
  acceptRules: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};

/**
 * Optional chat hook — returns null if outside ChatProvider.
 * Safe to call unconditionally (no hooks rules violation).
 */
export const useChatOptional = (): ChatContextValue | null => {
  return useContext(ChatContext);
};

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useAuth();
  const location = useLocation();
  const isAuthenticated = !!session;
  const isChatEnabled = useGlobalChatEnabled();
  useGlobalChatEnabledLive();
  const token = session?.token || null;
  const [messages, setMessages] = useState<ChatMessagePayload[]>([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [isOpen, setIsOpen] = useState(() => storage.getItem(CHAT_OPEN_KEY) === 'true');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [hasAcceptedRules, setHasAcceptedRules] = useState(() => storage.getItem(CHAT_RULES_KEY) === 'true');
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionExpiresAt, setSuspensionExpiresAt] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const cleanupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const suspensionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track open state in a ref so the message listener can read it without re-subscribing
  const chatVisibleRef = useRef(isOpen || isMobileOpen);

  // Keep visibility ref in sync and clear unread when chat opens
  useEffect(() => {
    chatVisibleRef.current = isOpen || isMobileOpen;
    if (isOpen || isMobileOpen) {
      setUnreadCount(0);
    }
  }, [isOpen, isMobileOpen]);

  // Remove expired messages client-side
  const pruneExpired = useCallback(() => {
    const cutoff = Date.now() - MESSAGE_TTL_MS;
    setMessages(prev => prev.filter(m => new Date(m.createdAt).getTime() > cutoff));
  }, []);

  // Start/stop the prune timer
  useEffect(() => {
    cleanupTimerRef.current = setInterval(pruneExpired, 60000); // every minute
    return () => {
      if (cleanupTimerRef.current) clearInterval(cleanupTimerRef.current);
    };
  }, [pruneExpired]);

  // Close panels when chat is disabled site-wide
  useEffect(() => {
    if (!isChatEnabled) {
      setIsOpen(false);
      setIsMobileOpen(false);
      setUnreadCount(0);
    }
  }, [isChatEnabled]);

  // Connect/disconnect chat socket based on auth and site config
  useEffect(() => {
    if (!isChatEnabled || !isAuthenticated || !token) {
      chatSocketClient.disconnect();
      setIsConnected(false);
      setMessages([]);
      return;
    }

    let mounted = true;

    const connect = async () => {
      try {
        await chatSocketClient.connect(token);
        if (mounted) {
          setIsConnected(true);
          chatSocketClient.requestHistory();
        }
      } catch (err) {
        console.error('[ChatContext] Failed to connect chat socket:', err);
        if (mounted) setIsConnected(false);
      }
    };

    connect();

    const unsubMessage = chatSocketClient.on('chat:message', (msg) => {
      if (mounted) {
        setMessages(prev => {
          // Deduplicate by id
          if (prev.some(m => m.id === msg.id)) return prev;
          // Keep max 100 messages in memory
          const next = [...prev, msg];
          return next.length > 100 ? next.slice(-100) : next;
        });
        // Increment unread if chat panel is not visible
        if (!chatVisibleRef.current) {
          setUnreadCount(prev => Math.min(prev + 1, 99));
        }
      }
    });

    const unsubHistory = chatSocketClient.on('chat:history', (data) => {
      if (mounted) {
        setMessages(data.messages);
      }
    });

    const unsubOnline = chatSocketClient.on('chat:online-count', (data) => {
      if (mounted) setOnlineCount(data.count);
    });

    const unsubDeleted = chatSocketClient.on('chat:message-deleted', (data) => {
      if (mounted) {
        const deletedId = Number(data.id);
        setMessages(prev => prev.filter(m => Number(m.id) !== deletedId));
      }
    });

    const unsubReviewed = chatSocketClient.on('chat:message-reviewed', (data) => {
      if (mounted) {
        const reviewedId = Number(data.id);
        setMessages(prev => prev.map(m => Number(m.id) === reviewedId ? { ...m, isReviewed: true } : m));
      }
    });

    const unsubSuspended = chatSocketClient.on('chat:suspended', (data: ChatSuspendedPayload) => {
      if (mounted) {
        setIsSuspended(true);
        setSuspensionExpiresAt(data.expiresAt);

        // If it's a temporary suspension, auto-clear when it expires
        if (data.expiresAt) {
          const ms = new Date(data.expiresAt).getTime() - Date.now();
          if (ms > 0) {
            if (suspensionTimerRef.current) clearTimeout(suspensionTimerRef.current);
            suspensionTimerRef.current = setTimeout(() => {
              setIsSuspended(false);
              setSuspensionExpiresAt(null);
            }, ms);
          }
        }
      }
    });

    const unsubUnbanned = chatSocketClient.on('chat:unbanned', () => {
      if (mounted) {
        setIsSuspended(false);
        setSuspensionExpiresAt(null);
        if (suspensionTimerRef.current) {
          clearTimeout(suspensionTimerRef.current);
          suspensionTimerRef.current = null;
        }
      }
    });

    return () => {
      mounted = false;
      unsubMessage();
      unsubHistory();
      unsubOnline();
      unsubDeleted();
      unsubReviewed();
      unsubSuspended();
      unsubUnbanned();
      if (suspensionTimerRef.current) clearTimeout(suspensionTimerRef.current);
      chatSocketClient.disconnect();
      setIsConnected(false);
    };
  }, [isChatEnabled, isAuthenticated, token]);

  // Persist desktop open state
  useEffect(() => {
    storage.setItem(CHAT_OPEN_KEY, String(isOpen));
  }, [isOpen]);

  // Close mobile chat on navigation
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const sendMessage = useCallback(async (message: string) => {
    return chatSocketClient.send(message);
  }, []);

  const flagMessage = useCallback(async (messageId: number) => {
    return chatSocketClient.flag(messageId);
  }, []);

  const unflagMessage = useCallback(async (messageId: number) => {
    return chatSocketClient.unflag(messageId);
  }, []);

  const toggleChat = useCallback(() => setIsOpen(prev => !prev), []);
  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleMobileChat = useCallback(() => setIsMobileOpen(prev => !prev), []);
  const openMobileChat = useCallback(() => setIsMobileOpen(true), []);
  const closeMobileChat = useCallback(() => setIsMobileOpen(false), []);

  const acceptRules = useCallback(() => {
    setHasAcceptedRules(true);
    storage.setItem(CHAT_RULES_KEY, 'true');
  }, []);

  return (
    <ChatContext.Provider value={{
      messages,
      onlineCount,
      unreadCount,
      isOpen,
      isMobileOpen,
      isConnected,
      hasAcceptedRules,
      isSuspended,
      suspensionExpiresAt,
      sendMessage,
      flagMessage,
      unflagMessage,
      toggleChat,
      openChat,
      closeChat,
      toggleMobileChat,
      openMobileChat,
      closeMobileChat,
      acceptRules,
    }}>
      {children}
    </ChatContext.Provider>
  );
};
